import {
  BarometerInHg,
  BarometerTrend,
  CloudCoverPercent,
  Waypoint,
  WindDirection,
  WindMph,
} from '@omniafishing/core';
import React, { useState } from 'react';
import { MapRef } from 'react-map-gl';
import { useWaypoints } from '../../../../hooks/use_waypoints';
import { FiltersScreenProps, WaypointType } from './filters_screen';
import { SharedWaypointModal } from './shared_waypoint_modal';
import { WaypointsListScreen } from './waypoints_list_screen';
import { WaypointDetailsScreen } from './waypoint_details_screen';
import { WaypointFormScreen } from './waypoint_form_screen';
import { WaypointImportScreen } from './waypoint_import_screen';

interface WaypointsModalProps {
  allWaypoints: Waypoint[];
  discardModal: (waypoint?: Waypoint) => void;
  editableWaypoint: Waypoint | null;
  isModalOpen: boolean;
  waypointsLoading: boolean;
  mapRef: MapRef;
  newWaypoint: Pick<Waypoint, 'lat' | 'lng' | 'waypoint_type' | 'name'> | null;
  onClose: () => void;
  onEditableWaypointChange: (waypoint: Waypoint | null, flyTo?: boolean) => void;
  onFilterChange: FiltersScreenProps['onFilterChange'];
  onNewWaypointChange: (wp: Pick<Waypoint, 'lat' | 'lng' | 'waypoint_type' | 'name'>) => void;
  onSelectedWaypointChange: (waypoint: Waypoint | null) => void;
  onSharedWaypointModalClose: () => void;
  onWaypointCreate: () => void;
  sharedWaypoint: Waypoint | null;
  selectedBarometricPressures: BarometerInHg[];
  selectedBarometricPressureTrends: BarometerTrend[];
  selectedCloudCovers: CloudCoverPercent[];
  selectedEndDate: string;
  selectedStartDate: string;
  selectedWaterbodyIds: string[];
  selectedWaypoint: Waypoint | null;
  selectedWaypointTypes: WaypointType[];
  selectedWindDirections: WindDirection[];
  selectedWindSpeeds: WindMph[];
  showWaypointDetailsBackButton: boolean;
  waypointsToRender: Waypoint[];
}
export const WaypointsModal = (props: WaypointsModalProps) => {
  const {
    allWaypoints,
    discardModal,
    editableWaypoint,
    isModalOpen,
    waypointsLoading,
    mapRef,
    newWaypoint,
    onClose,
    onEditableWaypointChange,
    onFilterChange,
    onNewWaypointChange,
    onSelectedWaypointChange,
    onSharedWaypointModalClose,
    onWaypointCreate,
    selectedWaterbodyIds,
    selectedWaypoint,
    selectedWaypointTypes,
    selectedBarometricPressureTrends,
    selectedBarometricPressures,
    selectedCloudCovers,
    selectedEndDate,
    selectedStartDate,
    selectedWindDirections,
    selectedWindSpeeds,
    sharedWaypoint,
    showWaypointDetailsBackButton,
    waypointsToRender,
  } = props;

  const [showImportScreen, setShowImportScreen] = useState(false);
  const { deleteWaypoint } = useWaypoints();

  const screens = {
    list: !newWaypoint && !selectedWaypoint && !editableWaypoint,
    create: newWaypoint || editableWaypoint,
    details: selectedWaypoint && !editableWaypoint,
    sharedWaypoint,
  };

  const handleWaypointDelete = (id: number) => {
    deleteWaypoint.mutate(id, {
      onSuccess: () => {
        onSelectedWaypointChange(null);
      },
    });
  };

  if (!isModalOpen) {
    return null;
  }

  if (screens.sharedWaypoint) {
    return <SharedWaypointModal onClose={onSharedWaypointModalClose} waypoint={sharedWaypoint} />;
  } else if (screens.details) {
    return (
      <WaypointDetailsScreen
        onClose={onClose}
        onEditableWaypointChange={onEditableWaypointChange}
        onSelectedWaypointChange={onSelectedWaypointChange}
        onWaypointDelete={handleWaypointDelete}
        showWaypointDetailsBackButton={showWaypointDetailsBackButton}
        waypoint={selectedWaypoint}
      />
    );
  } else if (showImportScreen) {
    return (
      <WaypointImportScreen onClose={onClose} onShowImportScreenChange={setShowImportScreen} />
    );
  } else if (screens.list) {
    return (
      <WaypointsListScreen
        allWaypoints={allWaypoints}
        onClose={onClose}
        onEditableWaypointChange={onEditableWaypointChange}
        onFilterChange={onFilterChange}
        onSelectedWaypointChange={onSelectedWaypointChange}
        onWaypointCreate={onWaypointCreate}
        onWaypointDelete={handleWaypointDelete}
        onWaypointImportScreenChange={setShowImportScreen}
        selectedWaterbodyIds={selectedWaterbodyIds}
        selectedWaypointTypes={selectedWaypointTypes}
        waypointsLoading={waypointsLoading}
        waypointsToRender={waypointsToRender}
        selectedBarometricPressures={selectedBarometricPressures}
        selectedBarometricPressureTrends={selectedBarometricPressureTrends}
        selectedCloudCovers={selectedCloudCovers}
        selectedEndDate={selectedEndDate}
        selectedStartDate={selectedStartDate}
        selectedWindDirections={selectedWindDirections}
        selectedWindSpeeds={selectedWindSpeeds}
      />
    );
  } else if (screens.create) {
    return (
      <WaypointFormScreen
        discardModal={discardModal}
        editableWaypoint={editableWaypoint}
        mapRef={mapRef}
        newWaypoint={newWaypoint}
        onEditableWaypointChange={onEditableWaypointChange}
        onNewWaypointChange={onNewWaypointChange}
        onSelectedWaypointChange={onSelectedWaypointChange}
        selectedWaypoint={selectedWaypoint}
      />
    );
  }
};
